'use client';

import dayjs from 'dayjs';
import useSWR from 'swr';

import Stack from '@mui/material/Stack';

import { useRouter } from 'src/routes/hooks';

import { DashboardContent } from 'src/layouts/dashboard';
import { paths } from 'src/routes/paths';
import { useAuthContext } from 'src/auth/hooks';
import { useAppContext } from '@/src/context/app';

import { hämtaBankkonton } from 'src/sections/bank/BankService';
import type { IBankkonto } from 'src/types/nya/ekonomi/bank/BankTyper';
import { EmailIntegrationService, type InkommandeEmail } from 'src/sections/email/EmailIntegrationService';
import { hämtaEllerSkapaBokslutProcess } from 'src/sections/bokforing/bokslut/BokslutProcessService';
import type { IBokslutProcess } from 'src/types/nya/ekonomi/bokforing/bokslutTyper';
import type { IOrganisation, IRäkenskapsår } from 'src/types/nya/common';

import DynamicDashboard, { type DashboardWidget } from './dynamic-dashboard';
import Välkomstbanner, { type Välkomstfakta } from '../valkomst/valkomstbanner';
import { useVälkomstläge } from '../valkomst/use-valkomstlage';
import { StartsidaVäljare, type Startsida } from '../components/startsida-valjare';
import { KORT_KATALOG, synligaKort } from '../kort-katalog';
import { formatKr, toDayjsSafe, swrOptions } from '../kort/kort-primitiver';

// ----------------------------------------------------------------------

function hittaRäkenskapsårLista(organisation: IOrganisation | undefined): IRäkenskapsår[] | undefined {
    if (!organisation || typeof organisation !== 'object') return undefined;
    for (const [nyckel, värde] of Object.entries(organisation as Record<string, unknown>)) {
        if (Array.isArray(värde) && värde.length > 0 && /r[aä]kensk/i.test(nyckel)) {
            return värde as IRäkenskapsår[];
        }
    }
    return undefined;
}

function senastAvslutadeKalenderårSomRäkenskapsår(): IRäkenskapsår {
    const föregåendeÅr = dayjs().subtract(1, 'year');
    return { förstaDag: föregåendeÅr.startOf('year'), sistaDag: föregåendeÅr.endOf('year') } as IRäkenskapsår;
}

function härledRäkenskapsår(organisation: IOrganisation | undefined): IRäkenskapsår {
    const list = hittaRäkenskapsårLista(organisation);
    if (!list || list.length === 0) return senastAvslutadeKalenderårSomRäkenskapsår();
    const idag = dayjs();
    const efterSistaDag = (a: IRäkenskapsår, b: IRäkenskapsår) =>
        (toDayjsSafe(b.sistaDag)?.valueOf() ?? 0) - (toDayjsSafe(a.sistaDag)?.valueOf() ?? 0);

    const avslutade = list
        .filter((r) => {
            const slut = toDayjsSafe(r.sistaDag);
            return slut && slut.isBefore(idag, 'day');
        })
        .sort(efterSistaDag);
    if (avslutade.length > 0) return avslutade[0];
    return [...list].sort(efterSistaDag)[0];
}

// ----------------------------------------------------------------------
// Summary pill hooks — kept lightweight since each card now owns its data
// ----------------------------------------------------------------------

function useSummaryData(orgId: string | undefined, organisation: IOrganisation | undefined, räkenskapsår: IRäkenskapsår | undefined) {
    const { data: bankkonton = [] } = useSWR<IBankkonto[]>(
        orgId ? ['bankkonton', orgId] : null,
        () => hämtaBankkonton(orgId!),
        swrOptions
    );

    const { data: email = [] } = useSWR<InkommandeEmail[]>(
        orgId ? ['inkommande-email', orgId] : null,
        () => EmailIntegrationService.hämtaMottagnaEmail(orgId!),
        swrOptions
    );

    const start = räkenskapsår?.förstaDag;
    const slut = räkenskapsår?.sistaDag;
    const startKey = start ? String((start as any).format?.('YYYY-MM-DD') ?? start) : '';
    const slutKey = slut ? String((slut as any).format?.('YYYY-MM-DD') ?? slut) : '';

    const { data: bokslutProcess } = useSWR<IBokslutProcess>(
        orgId && räkenskapsår ? ['bokslut-process', orgId, startKey, slutKey] : null,
        () => hämtaEllerSkapaBokslutProcess(orgId!, räkenskapsår!),
        swrOptions
    );

    return { bankkonton, email, bokslutProcess };
}

// ----------------------------------------------------------------------

export function FöreningsDashboard () {
    const { anvandare } = useAuthContext();
    const { organisation, valdOrganisation, aktuelltRäkenskapsår } = useAppContext();
    const router = useRouter();

    const orgType = valdOrganisation?.organisationsTyp;
    const verksamhet = organisation?.verksamhetsinriktning;

    const härlettRäkenskapsår = aktuelltRäkenskapsår ?? härledRäkenskapsår(organisation);

    const { bankkonton, email, bokslutProcess } = useSummaryData(
        organisation?.id,
        organisation,
        härlettRäkenskapsår
    );

    const { läge, idag } = useVälkomstläge();

    if (!organisation) {
        return null;
    }

    // ----- Fakta under remsan -----

    const totalBalans = bankkonton.reduce((sum, k) => sum + (k.saldo ?? 0), 0);
    const bokslutKlaraSteg = bokslutProcess?.stegStatus
        ? Object.values(bokslutProcess.stegStatus).filter((s) => s === 'KLAR').length
        : 0;
    const bokslutTotaltSteg = 6;

    const fakta: Välkomstfakta[] = [];
    if (email.length > 0) {
        fakta.push({
            etikett: `${email.length} mail i inkorgen`,
            ikon: 'solar:inbox-bold',
            href: paths.dashboard.inkorg,
        });
    }
    if (bankkonton.length > 0) {
        fakta.push({
            etikett: `${formatKr(totalBalans)} på ${bankkonton.length} ${bankkonton.length === 1 ? 'konto' : 'konton'}`,
            ikon: 'solar:card-bold',
            href: paths.dashboard.ekonomi.bank.root,
        });
    }
    if (bokslutProcess) {
        fakta.push({
            etikett: `Bokslut ${bokslutKlaraSteg} av ${bokslutTotaltSteg} steg`,
            ikon: 'solar:file-text-bold',
            href: paths.dashboard.ekonomi.bokforing.bokslut.root,
        });
    }

    const välkomst = (
        <Välkomstbanner
            läge={läge}
            idag={idag}
            förnamn={anvandare?.namn?.förnamn}
            organisation={{
                organisationsNamn: valdOrganisation?.organisationsNamn,
                organisationsTyp: orgType,
                verksamhetsinriktning: verksamhet,
            }}
            fakta={fakta}
        />
    );

    // ----- Cards from the catalog -----

    const kort = synligaKort(
        KORT_KATALOG,
        orgType as any,
        verksamhet,
        anvandare?.roller
    );

    const cardWidgets: DashboardWidget[] = kort.map((k) => ({
        id: `card-${k.id}`,
        size: { xs: 12, sm: 6, lg: 4 },
        component: <k.komponent orgId={organisation.id} räkenskapsår={härlettRäkenskapsår} />,
    }));

    const handleBytStartsida = (nästa: Startsida) => {
        if (nästa === 'kom-igang') {
            router.push(paths.dashboard.komIgång);
        }
    };

    const widgets: DashboardWidget[] = [
        { id: 'welcome', size: { xs: 12 }, component: välkomst },
        ...cardWidgets,
    ];

    return (
        <DashboardContent maxWidth="xl">
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                <StartsidaVäljare orgId={organisation.id} aktiv="oversikt" onByt={handleBytStartsida} />
            </Stack>
            <DynamicDashboard widgets={widgets} />
        </DashboardContent>
    );
}
